// Shared PWA install prompt management
// Used by InstallBanner and ProfileSettings

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function setDeferredPrompt(event: BeforeInstallPromptEvent | null) {
  deferredPrompt = event;
}

export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

export async function triggerInstall(): Promise<boolean> {
  if (!deferredPrompt) return false;
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice.outcome === 'accepted';
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as any).standalone === true;
}

export function isMobileDevice(): boolean {
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const narrowScreen = window.innerWidth < 1024;
  return hasTouch && narrowScreen;
}

export function isIOS(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// =============================================================================
// Web Push helpers
// =============================================================================

/**
 * Convert a VAPID public key (base64url) to the Uint8Array format
 * pushManager.subscribe expects via applicationServerKey.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Read the current browser-level Notification permission. Returns
 * 'unsupported' when the API isn't available (e.g., in incognito or in
 * iOS Safari before iOS 16.4 added support).
 */
export type NotificationPermissionState =
  | 'default'
  | 'granted'
  | 'denied'
  | 'unsupported';

export function getNotificationPermissionState(): NotificationPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission as NotificationPermissionState;
}

/**
 * Returns the active push subscription for the current PWA, or null if
 * none exists yet. Use this on app load to decide whether to re-sync the
 * subscription with the backend.
 */
export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (err) {
    console.warn('[pwaInstall] getCurrentPushSubscription failed:', err);
    return null;
  }
}

/**
 * Subscribe to web push using the VAPID public key. Returns the
 * subscription on success or null if push isn't supported or permission
 * was denied. Caller is responsible for sending the subscription to the
 * backend (e.g., insert into push_subscriptions).
 */
export async function subscribeToPush(
  vapidPublicKey: string,
): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('[pwaInstall] Service worker or PushManager not available');
    return null;
  }
  if (!vapidPublicKey) {
    console.warn('[pwaInstall] VAPID public key is empty — push not subscribed');
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    if (existing) return existing;

    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  } catch (err) {
    console.warn('[pwaInstall] subscribeToPush failed:', err);
    return null;
  }
}

/**
 * Unsubscribe from web push. Returns true if a subscription existed and
 * was successfully revoked.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return false;
    return await subscription.unsubscribe();
  } catch (err) {
    console.warn('[pwaInstall] unsubscribeFromPush failed:', err);
    return false;
  }
}
